import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useEffect, useState } from "react";
import { Cart } from "@bluebottlecoffee/design-system";
import { loadStripe } from "@stripe/stripe-js";
import { SanityDocument } from "@sanity/client";
import { parseCookie } from "../../../functions/lib/common";
import { useAnalytics } from "../../../lib/chord/packages/web/src/hooks/actions/use-analytics";
import { recommendClient } from "../../../lib/algolia/clients";
import sanityClient from "../../../lib/sanity-client";
import {
  Cart as CartSchema,
  OptionType,
  Product,
  ProductSubscriptionOption,
  QuantityLabel,
  ShopCollection,
  Subscription,
  SubscriptionInterval,
  Variant,
} from "../../../lib/sanity-schema";
import {
  toCartProps,
  toCartTableProps,
  toFreeShippingBarProps,
  toGiftMessageFormProps,
  toLineItemsProps,
  toOrderSummaryProps,
  toProductRecsCopyProps,
  toProductRecsProps,
  toRemovePromoAlertProps,
} from "../../../lib/transformers/cart";
import { useCart } from "../../../lib/chord/packages/web/src/hooks/actions/use-cart";
import {
  CartBaseProps,
  CartBasePropsWithSlug,
} from "../../../lib/sanity/shared";
import {
  cartVariants,
  discountTotal,
  getDefaultStaticPaths,
  getDefaultStaticPropsMiniCart,
  getPromoLabels,
  getPromoWithCode,
  isFreeShipping,
  shouldRenderFreeShippingBar,
} from "../../../lib/utils";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import {
  assignOnClick,
  isValidShopCard,
  ShopCardPropsWithSlug,
  toShopCardProps,
} from "../../../lib/transformers/shop-card";
import { AlgoliaRecommendProduct } from "../../../lib/algolia/types";
import { staticProductRecsQuery } from "../../../lib/sanity/cart-queries";
import { DereferencedShopCard } from "../../../lib/sanity/shop-card-queries";
import { findBestValueVariant } from "../../../lib/algolia/etl/shared";
import { DereferencedVariant } from "../../../lib/sanity/variant-queries";
import { ChordUseCartReturnType } from "../../../lib/chord/types";

export type SourceProductRecSubscription = Omit<Subscription, "intervals"> & {
  intervals: SubscriptionInterval;
};

export type SourceProductRecSubscriptionOption = Omit<
  ProductSubscriptionOption,
  "item" | "quantityLabel"
> & {
  item: Variant;
  quantityLabel: QuantityLabel;
};

export type SourceProductRec = Omit<
  Product,
  | "optionTypes"
  | "variants"
  | "shopCollections"
  | "subscription"
  | "subscriptionOptions"
  | "shopCard"
> & {
  optionTypes: OptionType[];
  shopCollections: ShopCollection[];
  subscription: SourceProductRecSubscription;
  subscriptionOptions: SourceProductRecSubscriptionOption[];
  shopCard?: DereferencedShopCard;
  variants: DereferencedVariant[];
  bestValueVariant: DereferencedVariant;
};

export const getStaticProps: GetStaticProps<CartBaseProps> = async (
  context,
) => {
  const { props: defaultProps } = await getDefaultStaticPropsMiniCart(context);

  const { lang } = defaultProps;
  const staticProductRecsDoc = await sanityClient().fetch<SanityDocument[]>(
    staticProductRecsQuery(lang),
  );
  const staticProductRecs: SourceProductRec[] =
    staticProductRecsDoc[0].products.map((product) => ({
      ...product,
      // Normally performed during indexing, static product recs are pulled
      // from their own Sanity collection to provide control over what
      // products are recommended. Any indexing functions on the product also
      // need to be done for recommendations on the cart.
      bestValueVariant: findBestValueVariant(
        product.name[lang],
        product.variants,
      ),
    }));

  return {
    props: {
      staticProductRecs,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: CartSchema) {
  if (!data.heading) {
    throw Error("Cart page must have a heading.");
  }
}

type PageData = CartBasePropsWithSlug & {
  staticProductRecs: SourceProductRec[];
};

const CartPage: FunctionComponent<PageData> = ({
  staticProductRecs,
  ...cartLayoutWrapperProps
}) => {
  const { trackClickProduct, trackViewCart } = useAnalytics();
  const { region, lang, cart: cartCopy } = cartLayoutWrapperProps;
  validate(cartCopy);
  const [promoSuccess, setPromoSuccess] = useState<boolean>(true);
  const [promoErrorMessage, setPromoErrorMessage] = useState<string>(
    cartCopy.orderSummary.promoCopy.errorMessage[lang],
  );
  const [productRecs, setProductRecs] = useState<
    SourceProductRec[] | AlgoliaRecommendProduct[]
  >(staticProductRecs);
  const [cartLoaded, setCartLoaded] = useState(false);
  const {
    addToCart,
    cart,
    loadCart,
    removeFromCart,
    modifyQuantity,
    addPromoCode,
    removePromoCode,
    modifyCart,
    checkout,
  }: ChordUseCartReturnType = useCart();
  const { data } = cart;

  useEffect(() => {
    loadCart()
      .then(() => {
        setCartLoaded(true);
      })
      .then(trackViewCart)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error.message);
      });
  }, [loadCart]);

  const onAddPromoCodeSubmit = async (promoCode: string) => {
    try {
      const promoResponse = await addPromoCode(promoCode);
      if (promoResponse.successful) {
        setPromoSuccess(true);
      } else {
        setPromoSuccess(false);
      }
    } catch (error) {
      setPromoSuccess(false);
      setPromoErrorMessage(error.message);
      console.error(error);
    }
  };

  const handleRemovePromoCode = async (promoCode: string) => {
    removePromoCode(promoCode);
    setPromoSuccess(true);
  };

  const onRemoveGiftMessage = async () => {
    try {
      await modifyCart({
        gift_note_attributes: {
          from: "",
          recipient: "",
          note: "",
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onAddGiftMessage = async (
    to: string,
    from: string,
    giftMessage?: string,
  ) => {
    try {
      await modifyCart({
        gift_note_attributes: {
          from,
          recipient: to,
          note: giftMessage,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Add Iterable campaign and template ID to Order metadata if values
   * are stored in cookies and castable to number
   *
   * IDs previously set on the Order metadata are removed if the relevant
   * cookies either a) have expired since the last time the method was
   * invoked, or b) no longer evaluate to a number.
   *
   * @returns { Promise<void> }
   */
  async function addOrRemoveCampaignMetadata(): Promise<void> {
    const { iterableEmailCampaignId, iterableTemplateId } = parseCookie();

    try {
      // A metadata key is removed by setting its value to an empty string:
      // https://chord.stoplight.io/docs/chord-oms/e8bbbcd029277-update-order
      await modifyCart({
        metadata: {
          campaignId: +iterableEmailCampaignId ? +iterableEmailCampaignId : "",
          templateId: +iterableTemplateId ? +iterableTemplateId : "",
        },
      });
    } catch (error) {
      console.error(
        "Failed to add Iterable campaign metadata to order:",
        error,
      );
    }
  }

  const onCheckoutSubmit = async () => {
    await addOrRemoveCampaignMetadata();

    const cart = await checkout();
    const stripe = await loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);
    stripe.redirectToCheckout({ sessionId: cart.checkoutSessionId });
  };

  function isCartEmpty(): boolean {
    return data.lineItems === null;
  }

  function isProductRecsDataValid(
    products: AlgoliaRecommendProduct[],
  ): boolean {
    const valid =
      products.length > 0 &&
      products
        ?.map(
          (product) =>
            product?.description[lang] &&
            product?.images?.length > 0 &&
            product?.name[lang] &&
            isValidShopCard(product as any) &&
            product?.slug?.current &&
            product?.variants
              .map(
                (variant) =>
                  variant?.images?.length > 0 &&
                  variant?.regularPrice &&
                  variant?.sku,
              )
              .every((res) => res) &&
            product?.bestValueVariant,
        )
        .every((res) => res);

    return valid;
  }

  function createRequestObjects() {
    const requests = data.lineItems.map((lineItem) => ({
      indexName: `${process.env.NEXT_PUBLIC_SANITY_STUDIO_API_DATASET}-products`,
      objectID: lineItem.variant.slug,
      maxRecommendations: 3,
      queryParameters: {
        filters: "forSale:true",
      },
      // TODO: define more focused fallbackParameters.
      // Empty string matches every object in index.
      fallbackParameters: {
        query: "",
        filters: "forSale:true",
      },
    }));
    return requests;
  }

  function sortRecommendations(algoliaProductRecs: AlgoliaRecommendProduct[]) {
    return algoliaProductRecs.sort((a, b) => b._score - a._score);
  }

  function removeDuplicateRecommendations(
    sortedAlgoliaProductRecs: AlgoliaRecommendProduct[],
  ) {
    // identify and remove recommendations with identical objectIDs
    // first (highest scoring) instance of an objectID will always remain
    return sortedAlgoliaProductRecs
      .filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.objectID === value.objectID),
      )
      .slice(0, 3);
  }

  async function getRecommendedProducts() {
    await recommendClient
      .getRelatedProducts(createRequestObjects())
      .then((res) => {
        const algoliaProductRecs = res.results.flatMap((result) => {
          // Hits from fallback query are not provided with a _score prop.
          // Set to 0 to allow them to work in sort function
          result.hits.forEach((rec: AlgoliaRecommendProduct) => {
            if (!rec._score) {
              // eslint-disable-next-line no-param-reassign
              rec._score = 0;
            }
          });
          return result.hits;
        }) as AlgoliaRecommendProduct[];
        const sortedAlgoliaProductRecs =
          sortRecommendations(algoliaProductRecs);

        const uniqueAlgoliaProductRecs = removeDuplicateRecommendations(
          sortedAlgoliaProductRecs,
        );

        // eslint-disable-next-line no-unused-expressions
        isProductRecsDataValid(uniqueAlgoliaProductRecs) &&
          setProductRecs(uniqueAlgoliaProductRecs);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error.message);
      });
  }

  useEffect(() => {
    if (cartLoaded && !isCartEmpty()) {
      getRecommendedProducts();
    }
  }, [cartLoaded]);

  function transformProductRecsToShopCards(): ShopCardPropsWithSlug[] {
    return productRecs.map((productRec) => {
      const shopCard = toShopCardProps(productRec, lang, region);

      return assignOnClick(shopCard, () =>
        trackClickProduct(shopCard.productSlug),
      );
    });
  }

  const transformedGiftNote =
    data.giftNote?.from && data.giftNote.recipient
      ? {
          to: data.giftNote.recipient,
          from: data.giftNote.from,
          giftMessage: data.giftNote.note,
        }
      : null;

  return (
    <LayoutWrapper {...cartLayoutWrapperProps}>
      <Cart
        {...toCartProps(cartCopy, region, lang)}
        cartTableProps={{
          ...toCartTableProps(cartCopy, lang),
          removeFromCart,
          modifyQuantity,
          cartLineItems: cartVariants({
            data,
            dialect: { region, lang },
          }),
        }}
        renderFreeShippingBar={shouldRenderFreeShippingBar(data)}
        freeShippingBarProps={{
          ...toFreeShippingBarProps(cartCopy.freeShippingBar, lang),
          estimatedTotal: {
            amount: Number(data.itemTotal) * 100,
            currency: data.currency,
          },
          freeShipPromoCode: isFreeShipping(data),
        }}
        orderSummaryProps={{
          ...toOrderSummaryProps(cartCopy.orderSummary, lang),
          lineItems: {
            ...toLineItemsProps(cartCopy.orderSummary.lineItems, lang),
            credit:
              data.totalApplicableStoreCredit !== "0.0"
                ? data.displayTotalApplicableStoreCredit
                : undefined,
            currency: data.currency,
            discountTotal: discountTotal(data),
            estimatedTotalCost: data.displayOrderTotalAfterStoreCredit
              ? data.displayOrderTotalAfterStoreCredit
              : undefined,
            freeShipping: isFreeShipping(data),
            giftMessage: transformedGiftNote,
            grossSubtotal: data.displayItemTotal
              ? data.displayItemTotal
              : cartCopy.orderSummary.lineItems.tbd[lang],
            promoLabels: getPromoLabels(data),
            shippingCost:
              data.shipTotal === "0.0" ? undefined : data.displayShipTotal,
            variantsTotal: data.totalQuantity ? data.totalQuantity : undefined,
          },
          giftMessageForm: {
            ...toGiftMessageFormProps(
              cartCopy.orderSummary.giftMessageCopy,
              lang,
            ),
            giftMessageProp: transformedGiftNote,
            removeGiftMessage: onRemoveGiftMessage,
            addGiftMessage: onAddGiftMessage,
          },
          promoForm: {
            error: !promoSuccess,
            promoCode: getPromoWithCode(data),
            promoCopy: {
              add: cartCopy.orderSummary.promoCopy.add[lang],
              apply: cartCopy.orderSummary.promoCopy.apply[lang],
              placeholder: cartCopy.orderSummary.promoCopy.placeholder[lang],
              errorMessage: promoErrorMessage,
            },
            ...toRemovePromoAlertProps(cartCopy.orderSummary.promoCopy, lang),
            removePromoCode: handleRemovePromoCode,
            addPromoCode: onAddPromoCodeSubmit,
          },
          /* TODO
           * subsPresent needs to be connected to data.subscriptionInCart, however, that
           * introduces a bug where if subscriptionInCart is true, then the user removes the subscription,
           * the Checkout Button remains inactive and the guest cannot checkout. @jesdeck
           */
          subsPresent: false,
          ctaClick: onCheckoutSubmit,
        }}
        productRecsProps={{
          addToCartClick: addToCart,
          emptyCart: isCartEmpty(),
          ...toProductRecsProps(productRecs, lang, data.currency),
          productRecShopCards: transformProductRecsToShopCards(),
          ...toProductRecsCopyProps(cartCopy, lang),
        }}
      />
    </LayoutWrapper>
  );
};

export default CartPage;
